import React, { useState, useEffect } from 'react';
import { Dropdown, Badge, Button } from 'react-bootstrap';
import { getBulkInvoicePDF } from "../../afinanceapi";
import './Notification.css';
import { useNavigate } from "react-router-dom";


function Notifications({ 
  notifications = [], 
  onRemoveNotification, 
  onMarkAsRead, 
  clearAllNotifications 
}) {
  const [show, setShow] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const unreadCount = notifications.filter(n => n.isRead === false).length;

 const handleNotificationClick = (notification) => {
  if (notification.isRead === false && onMarkAsRead) {
    onMarkAsRead(notification.id || notification._id);
  }

  // Deep link logic
  if (notification.type === "statement-run" && notification.meta?.runId) {
    navigate(`/userprofile?tab=tasks&runId=${notification.meta.runId}`);
    setShow(false);
    return;
  }

  if (notification.type === "era" && notification.meta?.eraLogId) {
    navigate(`/billing/era/${notification.meta.eraLogId}`);
    setShow(false);
    return;
  }

  // Fallback behavior for other notification types
  if (notification.downloadId) {
    handleDownload(notification);
  }
};


  const handleDownload = async (notification) => {
    if (!notification.downloadId) return;
    try {
      await getBulkInvoicePDF(notification.downloadId);
      onRemoveNotification(notification.downloadId || notification.id || notification._id);
    } catch (error) {
      console.error("Download failed:", error);
      onRemoveNotification(notification.downloadId || notification.id || notification._id);
    }
  };

  const handleDismiss = (e, notification) => {
    e.stopPropagation();
    onRemoveNotification(notification.id || notification._id);
  };

  const handleClearAll = () => {
    if (clearAllNotifications) {
      clearAllNotifications();
    } else {
      notifications.forEach(n => onRemoveNotification(n.id || n._id));
    }
    setShow(false);
  };

  return (
    <Dropdown
      show={show}
      onToggle={setShow}
      drop={isMobile ? "up" : "down"}
      align="end"  // Always align to RIGHT of bell (extends left) - works for both mobile and desktop
      flip
      popperConfig={{
        strategy: 'fixed',
        modifiers: [
          { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
          { 
            name: 'flip', 
            options: { 
              fallbackPlacements: isMobile ? ['top-end', 'bottom-end'] : ['bottom-end', 'top-end'] 
            } 
          },
          { name: 'offset', options: { offset: [0, 10] } },
        ],
      }}
    >
      <Dropdown.Toggle 
        variant="link" 
        className="notification-bell position-relative p-2"
        style={{ border: 'none', background: 'none' }}
      >
        <i className="fa-light fa-bell fs-6 text-primary"></i>
        {unreadCount > 0 && (
          <Badge 
            bg="danger" 
            pill 
            className="position-absolute top-0 start-100 translate-middle"
            style={{ fontSize: '0.7rem' }}
          >
            {unreadCount}
            <span className="visually-hidden">new notifications</span>
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-dropdown">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <h6 className="mb-0 text-primary">Notifications</h6>
          {notifications.length > 0 && (
            <Button 
              variant="link" 
              size="sm" 
              onClick={handleClearAll}
              className="p-0 text-secondary"
            >
              Clear All
            </Button>
          )}
        </div>

        {notifications.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-bell-slash text-primary" style={{ fontSize: '2rem' }}></i>
            <p className="mb-0 mt-2 text-primary">No new notifications</p>
          </div>
        ) : (
          notifications.map((notification, idx) => (
            <div
              key={notification.id || notification._id || `${notification.downloadId}-${notification.timestamp || idx}`}
              className={`notification-item px-3 py-2 border-bottom ${notification.isRead === false ? 'bg-light' : ''}`}
              style={{ cursor: 'pointer' }}
              onClick={() => handleNotificationClick(notification)}
            >
              <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center mb-1">
                    {notification.type === 'pdf' && <i className="fas fa-file-pdf text-danger me-2"></i>}
                    {notification.type === 'email' && <i className="fas fa-envelope text-primary me-2"></i>}
                    {notification.type === 'statement-run' && <i className="fas fa-tasks text-info me-2"></i>}
                    {notification.isRead === false && <Badge bg="primary" pill className="me-2">New</Badge>}
                  </div>

                  <p className="mb-1 small">{notification.message}</p>
                  {notification.type === 'statement-run' && notification.meta?.total > 0 && (
                    <div className="mb-1" style={{ height: 4, background: '#e9ecef', borderRadius: 2, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${Math.round(((notification.meta.processed || 0) / notification.meta.total) * 100)}%`,
                          height: '100%',
                          background: notification.meta.status === 'COMPLETED' ? '#198754' : notification.meta.status === 'COMPLETED_WITH_ERRORS' ? '#ffc107' : '#0d6efd',
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </div>
                  )}
                  <small className="text-muted">
                    {notification.timestamp ? new Date(notification.timestamp).toLocaleString() : 'Just now'}
                  </small>

                  {notification.downloadId && (
                    <div className="mt-2">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDownload(notification); }}
                      >
                        Download
                      </Button>
                    </div>
                  )}
                </div>

                <Button
                  variant="link"
                  size="sm"
                  className="text-muted p-0 ms-2"
                  onClick={(e) => handleDismiss(e, notification)}
                  title="Dismiss"
                >
                  <i className="fas fa-times"></i>
                </Button>
              </div>
            </div>
          ))
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default Notifications;